import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Card } from "@amzn/stencil-react-components/card";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { IconCheckCircleFill, IconExternalLink, IconSize } from "@amzn/stencil-react-components/icons";
import { DocumentStatus, getDocumentStatusInfo } from "../../utility/application-data";
import { getCandidateData } from "../../utility/candidate-data";
import { StatusEnum } from "../../hoc/with-status";
import { Text, H3 } from "../typography";
import { getLocale } from "../../utility/locale-helper";
import useArb from "../../hooks/use-arb";
import { addEventMetricWithBBId } from "../../utility/adobe-analytics";
import { DOCUMENT_STATUS_ENDPOINT } from "../../config/urls";
import { renderBGColor } from "../task/ui-redesign";
import { HVH_COLORS } from "../../config/palette";

export interface UrlParams {
  applicationId: string;
}

const ONBASE_COMPLETION_STATUS_COMPLETE = "Complete";
const ONBASE_COMPLETION_STATUS_PENDING = "Pending";
const ONBASE_COMPLETION_STATUS_ERROR = "Error";
const ONBASE_CANDIDATE_NOT_FOUND = "candidateNotFound";
const ADOBE_EVENT_START_EMPLOYMENT_PAPERWORK = "Start Employment Paperwork";
const ADOBE_EVENT_VIEW_EMPLOYMENT_PAPERWORK = "View Employment Paperwork";

export const EmploymentPaperworkButton = () => {
  const bundle = useArb("HVHChecklistPantherResource");

  const loadingText = bundle.getMessage("Checklist-MainPage-Document-Status-Loading");
  const startEmploymentPaperworkButton = bundle.getMessage(
    "Checklist-MainPage-Document-Status-Start-Employment-Paperwork"
  );

  const { applicationId } = useParams<UrlParams>();
  const locale = getLocale();
  const [loading, setLoading] = useState(false);
  const [documentStatusLoginUrl, setDocumentStatusLoginUrl] = useState<string>();
  const [documentCompletionStatus, setDocumentCompletionStatus] = useState<string>();

  const requestDocumentStatusLink = useCallback(async () => {
    setLoading(true);
    const candidateData = await getCandidateData(applicationId);

    let response = {} as DocumentStatus;
    if (candidateData && candidateData.candidateGlobalId) {
      try {
        response = await getDocumentStatusInfo(
          DOCUMENT_STATUS_ENDPOINT,
          candidateData.candidateGlobalId,
          applicationId,
          locale
        );
      } catch (e) {
        setDocumentCompletionStatus(ONBASE_COMPLETION_STATUS_ERROR);
      }

      if (response && response.results) {
        const results = response.results;
        results.forEach((item) => {
          if (item.onbaseLoginUrl && !item.error) {
            setDocumentCompletionStatus(item.documentCompletionStatus);
            setDocumentStatusLoginUrl(item.onbaseLoginUrl);
          } else {
            if (item.error === ONBASE_CANDIDATE_NOT_FOUND) {
              setDocumentCompletionStatus(item.documentCompletionStatus);
            } else {
              setDocumentCompletionStatus(ONBASE_COMPLETION_STATUS_ERROR);
            }
          }
        });
      }
    }

    setLoading(false);
  }, []);

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      await addEventMetricWithBBId(window, applicationId, eventName);
    },
    [applicationId]
  );

  const trackDocumentStatusClick = useCallback(() => sendAnalyticsEvent("Check Document Status"), [sendAnalyticsEvent]);

  useEffect(() => {
    requestDocumentStatusLink();
  }, []);

  const redirect = useCallback(() => {
    console.log("documentStatusLoginUrl: " + documentStatusLoginUrl);
    if (!documentStatusLoginUrl) {
      return;
    }

    if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_COMPLETE) {
      sendAnalyticsEvent(ADOBE_EVENT_VIEW_EMPLOYMENT_PAPERWORK);
    } else if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_PENDING) {
      sendAnalyticsEvent(ADOBE_EVENT_START_EMPLOYMENT_PAPERWORK);
    }

    window.open(documentStatusLoginUrl, "_blank");
  }, [documentStatusLoginUrl, trackDocumentStatusClick]);

  if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_COMPLETE) {
    return <></>;
  }

  if (loading) {
    return <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true}></Spinner>;
  }

  return (
    <Button size={ButtonSize.Small} variant={ButtonVariant.Primary} onClick={redirect}>
      <Row gridGap="0.5rem">
        <Col>{startEmploymentPaperworkButton}</Col>
        <Col>
          <IconExternalLink size={IconSize.ExtraSmall} />
        </Col>
      </Row>
    </Button>
  );
};

interface EmploymentPaperworkProps {
  status: StatusEnum;
}
export const EmploymentPaperwork = ({ status }: EmploymentPaperworkProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  const statusTitle = bundle.getMessage("checklist-General-Status");
  const completed = bundle.getMessage("checklist-General-TaskStatus-Completed");
  const actionRequired = bundle.getMessage("checklist-General-TaskStatus-ActionRequired");
  const dueSoon = bundle.getMessage("checklist-General-TaskStatus-DueSoon");
  const detailsTitle = bundle.getMessage("Checklist-CardControlled-DetailsTitle");
  const detailsText1 = bundle.getMessage("Checklist-MainPage-EmploymentPaperwork-Text1");
  const detailsText2 = bundle.getMessage("Checklist-MainPage-EmploymentPaperwork-Text2");

  if (status == StatusEnum.COMPLETED) {
    return (
      <Col className="flyout-content">
        <Col gridGap="1rem">
          <Card padding="0rem" title={statusTitle}>
            <Row>
              <Col backgroundColor={renderBGColor(status)} width={"6px"}></Col>
              <Col gridGap={"1rem"} padding={"1rem"}>
                <H3 fontSize={"T400"}>{statusTitle}</H3>
                <Row alignItems={"center"} gridGap={"0.5rem"}>
                  <IconCheckCircleFill size={IconSize.Medium} color={HVH_COLORS.GREEN_70} />
                  <Text>{completed}</Text>
                </Row>
              </Col>
            </Row>
          </Card>
          <Card>
            <Col gridGap={"1rem"}>
              <H3 fontSize={"T400"}>{detailsTitle}</H3>
              <Text>{detailsText2}</Text>
            </Col>
          </Card>
        </Col>
      </Col>
    );
  }

  return (
    <Col className="flyout-content">
      <Col gridGap="1rem">
        <Card padding="0rem" title={statusTitle}>
          <Row>
            <Col backgroundColor={renderBGColor(status)} width={"6px"}></Col>
            <Col gridGap={"1rem"} padding={"1rem"}>
              <H3 fontSize={"T400"}>{statusTitle}</H3>
              <Col gridGap="0.5rem">
                <Row alignItems={"center"}>
                  <Text>{actionRequired}</Text>
                </Row>
                <Row alignItems={"center"}>
                  <Text>{dueSoon}</Text>
                </Row>
              </Col>
            </Col>
          </Row>
        </Card>
        <Card>
          <Col gridGap={"1rem"}>
            <H3 fontSize={"T400"}>{detailsTitle}</H3>
            <Text>{detailsText1}</Text>
          </Col>
        </Card>
      </Col>
    </Col>
  );
};
